import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { FollowButton } from '../components/FollowButton';
import { MessageButton } from '../components/MessageButton';
import BookingModal from './BookingModal';
import ServiceCard from './ServiceCard';
import { API } from '../config/api';
import {
  Radio, MapPin, Share2, Clock, ExternalLink,
  Verified, FileText, Play, Users,
  Loader2, Calendar, Gamepad2, Mic2,
  Heart, Tv, Video, Eye, DollarSign
} from 'lucide-react';

const CATEGORY_META = {
  music: { icon: Mic2, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  gaming: { icon: Gamepad2, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  podcast: { icon: Mic2, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
  lifestyle: { icon: Heart, color: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/20' },
  irl: { icon: Tv, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  other: { icon: Radio, color: 'text-muted-foreground', bg: 'bg-muted/30', border: 'border-border/20' },
};

export default function StreamerProfileView({ profile, roleProfile, isOwnProfile, followerCount, followingCount, isFollowing, onFollowChange }) {
  const navigate = useNavigate();
  const [replays, setReplays] = useState([]);
  const [services, setServices] = useState([]);
  const [posts, setPosts] = useState([]);
  const [liveStream, setLiveStream] = useState(null);
  const [activeTab, setActiveTab] = useState('replays');
  const [loadingContent, setLoadingContent] = useState(true);
  const [showBooking, setShowBooking] = useState(false);

  const rp = roleProfile || {};
  const cp = rp.creative_profile || rp;
  const displayName = cp.display_name || rp.display_name || rp.stage_name || profile.name;
  const bio = cp.bio || rp.bio || profile.bio;
  const category = cp.stream_category || 'music';
  const subCategory = cp.stream_sub_category;
  const platforms = cp.platforms || rp.platforms || [];
  const socialLinks = cp.social_links || rp.social_links || {};
  const catMeta = CATEGORY_META[category] || CATEGORY_META.other;
  const CatIcon = catMeta.icon;

  useEffect(() => { fetchContent(); }, [profile.user_id]);

  const fetchContent = async () => {
    setLoadingContent(true);
    try {
      const [replayRes, svcRes, postRes, liveRes] = await Promise.all([
        fetch(`${API}/live/replays/user/${profile.user_id}`),
        fetch(`${API}/services/user/${profile.user_id}`),
        fetch(`${API}/posts?user_id=${profile.user_id}&limit=20`),
        fetch(`${API}/live/streams?host_id=${profile.user_id}&status=live`),
      ]);
      if (replayRes.ok) { const d = await replayRes.json(); setReplays(d.replays || []); }
      if (svcRes.ok) { const d = await svcRes.json(); setServices(d.services || []); }
      if (postRes.ok) { const d = await postRes.json(); setPosts(Array.isArray(d) ? d : d.posts || []); }
      if (liveRes.ok) { const d = await liveRes.json(); setLiveStream(d.streams?.[0] || null); }
    } catch (e) { console.error(e); }
    setLoadingContent(false);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/u/${profile.handle}`;
    try { await navigator.clipboard.writeText(url); toast.success('Profile link copied!'); }
    catch { toast.error('Could not copy link'); }
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return null;
    const s = parseInt(seconds);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div className="min-h-screen" data-testid="streamer-profile">
      {/* Banner */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        {(cp.banner_url || rp.banner_url || profile.banner_url) ? (
          <img src={cp.banner_url || rp.banner_url || profile.banner_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-red-900/40 via-zinc-900 to-orange-800/30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        {/* LIVE indicator */}
        {liveStream && (
          <div className="absolute top-4 right-4 z-10">
            <Badge className="bg-red-600 text-white animate-pulse px-3 py-1 text-sm cursor-pointer"
              onClick={() => navigate(`/watch/${liveStream.stream_id}`)} data-testid="live-now-badge">
              <Radio className="w-3 h-3 mr-1" />LIVE NOW
            </Badge>
          </div>
        )}
      </div>

      {/* Profile Header */}
      <div className="max-w-5xl mx-auto px-4 -mt-20 relative z-10">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          <div className="w-28 h-28 rounded-2xl border-4 border-background overflow-hidden bg-muted flex-shrink-0 shadow-xl">
            {(cp.avatar_url || rp.avatar_url || profile.avatar_url || profile.picture) ? (
              <img src={cp.avatar_url || rp.avatar_url || profile.avatar_url || profile.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Radio className="w-10 h-10 text-red-500/50" /></div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-2xl md:text-3xl font-bold">{displayName}</h1>
              {(rp.is_verified || profile.is_verified) && <Verified className="w-5 h-5 text-red-400" />}
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><Radio className="w-3 h-3 mr-1" />Streamer</Badge>
            </div>
            <p className="text-muted-foreground text-sm">{profile.handle ? `@${profile.handle}` : ""}</p>

            {/* Category */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge className={`${catMeta.bg} ${catMeta.color} ${catMeta.border}`}>
                <CatIcon className="w-3 h-3 mr-1" />{category.charAt(0).toUpperCase() + category.slice(1)}
              </Badge>
              {subCategory && <Badge variant="outline" className="text-xs">{subCategory}</Badge>}
            </div>

            {/* Location + Platforms */}
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              {(cp.neighborhood || profile.neighborhood || profile.city || profile.location) && (
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{cp.neighborhood || profile.neighborhood || profile.city || profile.location}</span>
              )}
              {platforms.length > 0 && platforms.map(p => (
                <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
              ))}
            </div>

            {/* Stats */}
            <div className="flex items-center gap-5 mt-3">
              <div className="text-center"><p className="font-bold text-lg">{followerCount}</p><p className="text-xs text-muted-foreground">Followers</p></div>
              <div className="text-center"><p className="font-bold text-lg">{followingCount}</p><p className="text-xs text-muted-foreground">Following</p></div>
              <div className="text-center"><p className="font-bold text-lg">{replays.length}</p><p className="text-xs text-muted-foreground">Replays</p></div>
            </div>

            {/* Action Buttons */}
            {!isOwnProfile && (
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <FollowButton targetUserId={profile.user_id} initialIsFollowing={isFollowing} onFollowChange={onFollowChange} />
                <MessageButton targetUserId={profile.user_id} targetName={displayName} variant="outline" size="default" showText />
                {liveStream ? (
                  <Button onClick={() => navigate(`/watch/${liveStream.stream_id}`)} className="bg-red-600 hover:bg-red-700 text-white animate-pulse" data-testid="watch-live-btn">
                    <Radio className="w-4 h-4 mr-2" />Watch Live
                  </Button>
                ) : (
                  <Button onClick={() => setShowBooking(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="book-streamer-btn">
                    <Calendar className="w-4 h-4 mr-2" />Book Streamer
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={handleShare} data-testid="share-streamer-btn"><Share2 className="w-4 h-4" /></Button>
              </div>
            )}
            {isOwnProfile && (
              <div className="flex items-center gap-2 mt-4">
                <Button className="bg-red-600 hover:bg-red-700" onClick={() => navigate('/live')} data-testid="go-live-btn">
                  <Radio className="w-4 h-4 mr-2" />Go Live
                </Button>
                <Button variant="outline" onClick={() => navigate('/dashboard/streamer')} data-testid="manage-streamer-btn">
                  <Video className="w-4 h-4 mr-2" />Manage
                </Button>
                <Button variant="ghost" size="icon" onClick={handleShare}><Share2 className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        {bio && <p className="mt-5 text-sm text-muted-foreground max-w-2xl leading-relaxed">{bio}</p>}

        {/* Social Links */}
        {Object.keys(socialLinks).length > 0 && (
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {Object.entries(socialLinks).filter(([, v]) => v).map(([k, v]) => (
              <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-red-400 transition-colors flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />{k}
              </a>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="w-full justify-start bg-muted/30 p-1 mb-6 overflow-x-auto">
            <TabsTrigger value="replays" className="flex items-center gap-2" data-testid="stream-tab-replays">
              <Video className="w-4 h-4" />Replays{replays.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{replays.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2" data-testid="stream-tab-services">
              <Calendar className="w-4 h-4" />Services{services.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{services.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="posts" className="flex items-center gap-2" data-testid="stream-tab-posts">
              <FileText className="w-4 h-4" />Posts{posts.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{posts.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Replays Tab */}
          <TabsContent value="replays">
            {loadingContent ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : replays.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No replays yet</h3>
                <p className="text-muted-foreground">{isOwnProfile ? 'Your saved stream replays will appear here.' : 'This streamer hasn\'t saved any replays yet.'}</p>
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {replays.map(r => (
                  <Card key={r.replay_id} className="glass-card border-border overflow-hidden cursor-pointer hover:border-red-500/30 transition-all group"
                    onClick={() => navigate(`/live/replay/${r.replay_id}`)} data-testid={`replay-card-${r.replay_id}`}>
                    <div className="aspect-video bg-card relative flex items-center justify-center">
                      {r.cover_image || r.thumbnail_url ? (
                        <img src={r.cover_image || r.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Play className="w-10 h-10 text-red-400/50 group-hover:text-red-400 transition-colors" />
                      )}
                      {r.duration_seconds && (
                        <Badge className="absolute bottom-2 right-2 bg-background/70 text-foreground text-xs">
                          <Clock className="w-3 h-3 mr-1" />{formatDuration(r.duration_seconds)}
                        </Badge>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-sm truncate">{r.title}</h4>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{r.views || 0} views</span>
                        <span>{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            {services.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No services listed</h3>
                <p className="text-muted-foreground">{isOwnProfile ? 'Add services from your dashboard.' : 'No services listed yet.'}</p>
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {services.map(svc => <ServiceCard key={svc.service_id} service={svc} onBook={!isOwnProfile ? () => setShowBooking(true) : null} />)}
              </div>
            )}
          </TabsContent>

          {/* Posts Tab */}
          <TabsContent value="posts">
            {posts.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No posts yet</h3>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map(p => (
                  <Card key={p.post_id} className="glass-card border-border p-4 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => navigate(`/post/${p.post_id}`)}>
                    <p className="text-sm">{p.text || p.content}</p>
                    {p.media_url && <img src={p.media_url} alt="" className="mt-3 rounded-lg max-h-64 object-cover" />}
                    <p className="text-xs text-muted-foreground mt-2">{new Date(p.created_at).toLocaleDateString()}</p>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {showBooking && (
        <BookingModal
          targetId={profile.user_id}
          targetName={displayName}
          targetRole="streamer"
          services={services}
          onClose={() => setShowBooking(false)}
        />
      )}
    </div>
  );
}
